import { AlertCircle } from 'lucide-react'
import { useParams } from 'react-router'

import { Badge, Divider, EmptyState, Heading, Switch, TabContent, TabItem, Tabs, Text } from '@ankor-io/ui-components'
import { Loading } from '@ankor-io/ui-components'

import { OrganizationFees } from '@/components/organizations/pricing/organization-fees'
import { useOrganizationDetailsQuery } from '@/queries/organization'
import { getOrganizationDisplayDescription } from '@/utils/organization'

export function OrganizationPricing() {
  const { id } = useParams<{ id: string }>()
  const { data: organization, error, isPending } = useOrganizationDetailsQuery(id)

  // Error toast is handled globally by QueryProvider
  // Component still handles error UI state (EmptyState below)

  if (!id) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Text className="text-red-600">Organization ID is required</Text>
        </div>
      </div>
    )
  }

  if (isPending) {
    return <Loading variant="spinner" />
  }

  if (error) {
    return (
      <EmptyState
        heading="Failed to load organization"
        subheading={error?.message || 'An error occurred while loading organization details.'}
        icon={<AlertCircle className="mx-auto size-12 text-gray-400" />}
      />
    )
  }

  return (
    <div className="space-y-4 relative min-h-[calc(100vh-7.5rem)] pb-10">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Heading level={2}>{organization?.name}</Heading>
        </div>

        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-white">Id:</span>
            <Badge color="gray">{organization?.id}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-white">Users:</span>
            <Badge color="gray">{organization?.userCount || 0}</Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-900 dark:text-white">Visibility:</span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {organization?.visibility === 'visible' ? 'Visible' : 'Hidden'}
              </span>
              <Switch
                checked={organization?.visibility === 'visible'}
                disabled
              />
            </div>
          </div>
        </div>

        <Text className="max-w-2xl text-gray-600 dark:text-gray-400">
          {getOrganizationDisplayDescription(organization)}
        </Text>

        <Divider />
      </div>

      <Tabs>
        <TabItem
          current={false}
          to={`/organizations/${id}/vessels`}
        >
          Vessels
        </TabItem>
        <TabItem
          current={false}
          to={`/organizations/${id}`}
        >
          Feature Flags
        </TabItem>
        <TabItem
          current={true}
          to={`/organizations/${id}/pricing`}
        >
          Pricing
        </TabItem>
        <TabItem
          current={false}
          to={`/organizations/${id}/integrations`}
        >
          Integrations
        </TabItem>
        <TabItem
          current={false}
          to={`/organizations/${id}/pipelines`}
        >
          Pipelines
        </TabItem>
      </Tabs>

      <TabContent isActive={true}>
        <OrganizationFees organizationId={id} />
      </TabContent>
    </div>
  )
}
